export const DEFAULT_PRICE_MULTIPLIER = 1.45;

export const MIN_PRICE_MULTIPLIER = 1.1;

export const MAX_PRICE_MULTIPLIER = 3;

export const MERCH_PRICE_MULTIPLIER = DEFAULT_PRICE_MULTIPLIER;

export function normalizePriceMultiplier(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value ?? ""));
  if (!Number.isFinite(parsed)) return DEFAULT_PRICE_MULTIPLIER;
  return Math.min(Math.max(Math.round(parsed * 100) / 100, MIN_PRICE_MULTIPLIER), MAX_PRICE_MULTIPLIER);
}

export const PRINT_DPI = 300;

export const MAX_PRINT_MEGAPIXELS = 24;

export const PRINT_INSET = 0.92;

export const MIN_PRINT_DPI = 150;

export const MERCH_CURRENCY = "USD";

export const MERCH_CATEGORIES = ["tee", "hoodie", "sweatshirt", "tank"] as const;

export type MerchCategory = (typeof MERCH_CATEGORIES)[number];

export const MERCH_CATEGORY_LABELS: Record<MerchCategory, string> = {
  tee: "T-shirt",
  hoodie: "Hoodie",
  sweatshirt: "Sweatshirt",
  tank: "Tank top",
};

export function retailPriceCents(costCents: number, multiplier: number = MERCH_PRICE_MULTIPLIER): number {
  if (!Number.isFinite(costCents) || costCents <= 0) return 0;
  const marked = Math.ceil(costCents * multiplier);
  const wholeDollars = Math.ceil((marked - 99) / 100);
  return Math.max(99, wholeDollars * 100 + 99);
}

export function marginCents(retailCents: number, costCents: number): number {
  return Math.max(0, Math.round(retailCents) - Math.round(costCents));
}

export function marginPercent(retailCents: number, costCents: number): number {
  if (retailCents <= 0) return 0;
  return Math.round((marginCents(retailCents, costCents) / retailCents) * 1000) / 10;
}

export function parseProdigiAmount(amount: string | number | null | undefined): number {
  if (typeof amount === "number") return Math.round(amount * 100);
  const parsed = Number.parseFloat(String(amount ?? "").trim());
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed * 100);
}

export function formatMoney(cents: number, currency: string = MERCH_CURRENCY, locale = "en-US"): string {
  const safe = Number.isFinite(cents) ? cents : 0;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(safe / 100);
  } catch {
    return `${(safe / 100).toFixed(2)} ${currency}`;
  }
}

export interface PixelSize {
  widthPx: number;
  heightPx: number;
}

export interface CappedPrintArea extends PixelSize {
  megapixels: number;
  scaledDown: boolean;
}

export function capPrintArea(area: PixelSize, maxMegapixels: number = MAX_PRINT_MEGAPIXELS): CappedPrintArea {
  const width = Math.max(1, Math.round(area.widthPx));
  const height = Math.max(1, Math.round(area.heightPx));
  const ceiling = Math.max(1, maxMegapixels) * 1_000_000;
  const pixels = width * height;
  if (pixels <= ceiling) {
    return { widthPx: width, heightPx: height, megapixels: round(pixels / 1_000_000, 2), scaledDown: false };
  }
  const scale = Math.sqrt(ceiling / pixels);
  const cappedWidth = Math.max(1, Math.floor(width * scale));
  const cappedHeight = Math.max(1, Math.floor(height * scale));
  return {
    widthPx: cappedWidth,
    heightPx: cappedHeight,
    megapixels: round((cappedWidth * cappedHeight) / 1_000_000, 2),
    scaledDown: true,
  };
}

export interface PrintPlacementInput {
  area: PixelSize;
  source: PixelSize;
  inset?: number;
  maxMegapixels?: number;
}

export interface PrintPlacement {
  canvasWidth: number;
  canvasHeight: number;
  boxWidth: number;
  boxHeight: number;
  artWidth: number;
  artHeight: number;
  offsetX: number;
  offsetY: number;
  canvasScaledDown: boolean;
  effectiveDpi: number;
  meetsMinimumDpi: boolean;
}

export function printPlacement(input: PrintPlacementInput): PrintPlacement {
  const inset = clamp(input.inset ?? PRINT_INSET, 0.1, 1);
  const canvas = capPrintArea(input.area, input.maxMegapixels ?? MAX_PRINT_MEGAPIXELS);
  const boxWidth = Math.max(1, Math.round(canvas.widthPx * inset));
  const boxHeight = Math.max(1, Math.round(canvas.heightPx * inset));
  const sourceWidth = Math.max(1, Math.round(input.source.widthPx));
  const sourceHeight = Math.max(1, Math.round(input.source.heightPx));
  const scale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
  const artWidth = Math.max(1, Math.round(sourceWidth * scale));
  const artHeight = Math.max(1, Math.round(sourceHeight * scale));
  const printedWidthPx = artWidth * (Math.max(1, Math.round(input.area.widthPx)) / canvas.widthPx);
  const printedWidthInches = printedWidthPx / PRINT_DPI;
  const effectiveDpi = printedWidthInches > 0 ? Math.round(sourceWidth / printedWidthInches) : 0;
  return {
    canvasWidth: canvas.widthPx,
    canvasHeight: canvas.heightPx,
    boxWidth,
    boxHeight,
    artWidth,
    artHeight,
    offsetX: Math.round((canvas.widthPx - artWidth) / 2),
    offsetY: Math.round((canvas.heightPx - artHeight) / 2),
    canvasScaledDown: canvas.scaledDown,
    effectiveDpi,
    meetsMinimumDpi: effectiveDpi >= MIN_PRINT_DPI,
  };
}

export function printAreaInches(area: PixelSize, dpi: number = PRINT_DPI): { width: number; height: number } {
  return { width: round(area.widthPx / dpi, 1), height: round(area.heightPx / dpi, 1) };
}

export function printTransformation(placement: PrintPlacement): string {
  return [
    `c_fit,w_${placement.boxWidth},h_${placement.boxHeight}`,
    `c_pad,w_${placement.canvasWidth},h_${placement.canvasHeight},b_transparent`,
    "f_png,q_auto:best,fl_lossy.strip_profile",
  ].join("/");
}

export function printReadyUrl(input: { cloudName: string; publicId: string; placement: PrintPlacement }): string {
  const id = input.publicId.replace(/^\/+/, "");
  return `https://res.cloudinary.com/${input.cloudName}/image/upload/${printTransformation(input.placement)}/${id}.png`;
}

export interface DesignFaceState {
  faceDetected?: boolean | null;
  faceCheckedAt?: number | null;
  source?: string | null;
  likenessApprovedAt?: number | null;
  likenessApprovedBy?: string | null;
}

export const FACE_RULE_MESSAGE =
  "This design shows a recognisable face. It stays off sale until the artist approves this exact artwork from the admin.";

export const FACE_UNCHECKED_MESSAGE =
  "This design has not been through the face check yet. Run the curation before making it eligible.";

export const NOT_TRUEVINED_MESSAGE = "not Truevined artwork";

export const STOREFRONT_DESIGN_SOURCES = ["release_cover", "artwork_cut", "logo"] as const;

export type StorefrontDesignSource = (typeof STOREFRONT_DESIGN_SOURCES)[number];

export function isOfferableSource(source?: string | null): boolean {
  return (STOREFRONT_DESIGN_SOURCES as readonly string[]).includes(String(source ?? ""));
}

export function isRetiredDesignTag(design: DesignFaceState): boolean {
  return !isOfferableSource(design.source);
}

/**
 * The artist's own sign-off on one piece of artwork that the face check flagged.
 * It is per design and per image: replacing the image clears it along with the
 * face verdict, so an approval can never carry over to a file nobody approved.
 */
export function hasLikenessApproval(design: DesignFaceState): boolean {
  return (
    typeof design.likenessApprovedAt === "number" &&
    design.likenessApprovedAt > 0 &&
    typeof design.likenessApprovedBy === "string" &&
    design.likenessApprovedBy.trim().length > 0
  );
}

export function designEligibilityBlock(design: DesignFaceState): string | null {
  if (!isOfferableSource(design.source)) return NOT_TRUEVINED_MESSAGE;
  if (design.faceDetected === true && !hasLikenessApproval(design)) return FACE_RULE_MESSAGE;
  if (typeof design.faceCheckedAt !== "number" || design.faceCheckedAt <= 0) return FACE_UNCHECKED_MESSAGE;
  return null;
}

export function canBeEligible(design: DesignFaceState): boolean {
  return designEligibilityBlock(design) === null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function round(value: number, places: number): number {
  const factor = Math.pow(10, places);
  return Math.round(value * factor) / factor;
}
